// Story #5648 lane B3 — contract row C4, Figure 15's "Widest view" control.
// The widest scope any member of this organisation may open, with the lock
// that re-asserts the organisation layer over every member's personal choice.
import { SPINE_LEVEL_LABEL } from '../spine'
import { shortDate } from '../../lib/shortDate'

const OPTIONS: { value: 'entity' | 'portfolio' | 'no_limit'; label: string }[] = [
  { value: 'entity', label: 'Stop at one Entity' },
  { value: 'portfolio', label: 'Stop at the Portfolio' },
  { value: 'no_limit', label: 'No limit' },
]

interface WidestViewPolicyProps {
  value: 'entity' | 'portfolio' | 'no_limit'
  locked: boolean
  source?: string
  orgName: string
  onChange: (next: 'entity' | 'portfolio' | 'no_limit') => void
  onLockChange: (next: boolean) => void
  /**
   * Bug #5846 — the four-layer ladder Figure 15 draws under this control.
   * Every input is live: `source` says which layer is deciding right now
   * (preferences/handler._resolve), the two starting values come from that
   * same module's PLATFORM_DEFAULTS and ORG_TYPE_PACKS, and who/when come
   * from the organisation row's own prefs_updated_by_name / prefs_updated_at.
   * The generic "Preference precedence" card elsewhere on the page cannot
   * supply this: widest_view is not one of its GOVERNABLE_KEYS.
   */
  platformValue?: 'entity' | 'portfolio' | 'no_limit'
  orgTypeValue?: 'entity' | 'portfolio' | 'no_limit'
  /** true when this organisation has a widest_view value of its own */
  policySet?: boolean
  policyUpdatedAt?: string | null
  policyUpdatedByName?: string | null
}

/** "Portfolio · Entity · Across portfolios" — the pack's visible effect, read
 *  from the spine's own level labels, exactly as OrgVocabularyPanel reads them. */
const VOCABULARY_TRIPLE = [
  SPINE_LEVEL_LABEL.portfolio, SPINE_LEVEL_LABEL.entity, SPINE_LEVEL_LABEL.across,
].join(' · ')

const VALUE_LABEL: Record<string, string> = {
  entity: 'Stop at one Entity', portfolio: 'Stop at the Portfolio', no_limit: 'No limit',
}

interface LadderRow {
  n: number
  /** The figure gives each layer its own mark, so the four are told apart at a
   *  glance rather than by reading four headings. */
  icon: string
  title: string
  value: string
  state: 'in-effect' | 'locked-in-effect' | 'set-not-in-effect' | 'blocked' | 'not-set'
  detail: string
}

/**
 * "18 Aug 2026" — Figure 15's own date form on the policy rung.
 *
 * The shared formatter, NOT toLocaleDateString('en-AU', { month: 'short' }),
 * which is what this used to call. en-AU's ICU data abbreviates September to
 * FOUR letters — alone among the twelve — so this rung printed "4 Sept 2026"
 * while every other date in the build printed "Sep". One page, two spellings of
 * one month, and no test could see it because both were "correct".
 */
const policyDate = shortDate

/** "set by …" -> "Set by …". A rung's detail is a sentence of its own. */
function capitalise(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
}

function buildLadder(
  source: string | undefined, locked: boolean, orgName: string,
  platformValue: string, orgTypeValue: string, policyValue: string, policySet: boolean,
  updatedAt: string | null | undefined, updatedByName: string | null | undefined,
): LadderRow[] {
  const winner = source === 'org_policy_locked' || source === 'org_policy' ? 3
    : source === 'org_type' ? 2
    : source === 'platform' ? 1
    : source === 'personal' ? 4
    : 0 // unknown source — no row is claimed to be in effect

  const when = policyDate(updatedAt)
  // Figure C4's rung 3 reads "set by Roger Price on 18 Aug 2026 and locked".
  // WHO set it is the point of the sentence — a lock with no author is a rule
  // nobody can go and ask about.
  //
  // The actor comes from the organisation's own policy record
  // (prefs_updated_by_name), which the route back-fills from the directory when
  // the stored name is empty.
  //
  // ROUND 4: when nothing resolves, the sentence keeps its SUBJECT. Round 3
  // dropped the "set by" clause entirely and the rung read "set on 4 Sept 2026
  // and locked…" — a sentence with no subject at all, which is worse than the
  // round-2 version it replaced: a lock nobody appears to have set is a rule
  // nobody can go and ask about. So it names the class of person who is
  // necessarily behind it — only an owner or admin can write this policy — and
  // never a specific person it has not actually read.
  //
  // The gap is legacy data, not a missing code path: the write path stores both
  // prefs_updated_by_name and prefs_updated_by (the sub, so a name unresolvable
  // at write time can be resolved at read time), and Acme's live row was
  // written on 4 Sep 2026 before either field existed. Every policy saved from
  // now on names its author.
  const who = (updatedByName || '').trim()
  const setBy = who || 'the organisation\u2019s owner'
  const provenance = when ? `set by ${setBy} on ${when}` : `set by ${setBy}`

  // The rung's detail is its OWN sentence and starts with a capital. Round 3
  // moved the value up into the pill and left the detail continuing a sentence
  // whose first half had gone, so rung 3 opened mid-flow with a lowercase
  // "set on 4 Sept 2026…". It still never repeats the value the pill carries —
  // it just begins properly.
  const policyDetail = !policySet
    ? 'This organisation has no widest_view policy, so the layer above applies.'
    : capitalise(provenance)
      + (locked
        ? ' and locked, so it re-asserts over every member\u2019s personal choice in this'
          + ' organisation\u2019s workspaces.'
        : ', and not locked, so a member may still set their own value.')

  return [
    {
      n: 1, icon: '🛡️', title: 'Platform default',
      // "No limit · fallback" — the figure's own pill text for this rung, and
      // the state suffix for rung 1 is null so it is not doubled.
      value: `${VALUE_LABEL[platformValue] || platformValue} · fallback`,
      state: winner === 1 ? 'in-effect' : 'not-set',
      detail: 'InnovateFlow never caps anyone. Used only when no organisation type, policy or'
        + ' personal value resolves.',
    },
    {
      n: 2, icon: '🏷️', title: 'Organisation-type pack',
      value: VALUE_LABEL[orgTypeValue] || orgTypeValue,
      state: winner === 2 ? 'in-effect' : winner > 2 ? 'set-not-in-effect' : 'not-set',
      // The vocabulary triple is named, not summarised: the pack's whole visible
      // effect is those three words, and the reader can check them against the
      // Vocabulary card on the same screen. They come from the same live source
      // that card reads — the spine's own level labels — so the two can never
      // disagree.
      detail: `The pack this organisation resolves to supplies the vocabulary — ${VOCABULARY_TRIPLE}`
        + ' — and a starting Widest view. It is never printed as an org-type name, because no'
        + ' token produces a readable one.',
    },
    {
      n: 3, icon: '🏢', title: `Organisation policy · ${orgName}`,
      value: policySet ? (VALUE_LABEL[policyValue] || policyValue) : 'Not set',
      state: winner === 3 ? (locked ? 'locked-in-effect' : 'in-effect') : 'not-set',
      detail: policyDetail,
    },
    {
      n: 4, icon: '👤', title: 'Personal choice · each member',
      // The chip says "Blocked by the lock"; the value must not repeat it.
      value: locked ? 'A member may only go lower' : 'A member may go lower',
      state: locked ? 'blocked' : winner === 4 ? 'in-effect' : 'not-set',
      detail: locked
        ? '\u201cSimple mode — work one invention at a time\u201d is the only value their select'
          + ' offers while the policy is locked.'
        : 'their own value wins unless this policy is locked.',
    },
  ]
}

/** The honest reason the active layer won — read from the live source. */
function whyThisIsActive(source: string | undefined, locked: boolean): string {
  if (source === 'org_policy_locked' || (source === 'org_policy' && locked)) {
    return 'organisation policy is locked \u2192 it outranks the personal layer below and the type'
      + ' pack above. Members see this row with the chip \u201cSet by your organisation\u2019s'
      + ' policy\u201d; a personal write returns 403 naming the lock.'
  }
  if (source === 'org_policy') {
    return 'this organisation has set a widest_view policy and no member has overridden it, so the'
      + ' policy layer decides. It is not locked, so a member still may.'
  }
  if (source === 'org_type') {
    return 'no organisation policy is set, so the type pack\u2019s starting value applies to every'
      + ' member of this organisation.'
  }
  if (source === 'personal') {
    return 'this setting is not locked and the member has chosen their own value, so the personal'
      + ' layer wins over both organisation layers.'
  }
  if (source === 'platform') {
    return 'no organisation type, policy or personal value resolves, so the platform fallback'
      + ' applies.'
  }
  return 'the resolved source for this setting was not returned, so no layer is claimed to be in'
    + ' effect.'
}

// Figure C4's own connector captions, in its own words. Derived from the row
// titles before, which produced "overridden by organisation policy · meridian
// ventures" — the organisation's name lower-cased mid-sentence, which the figure
// does not say and which reads as a mistake.
const LADDER_CONNECTOR = [
  '↓ overridden by the organisation-type pack',
  '↓ overridden by organisation policy',
  '↓ would be overridden by',
]

// The state chips beside a rung's heading. "● In effect" is rendered separately
// (every active rung carries it, locked or not — the figure shows both chips on
// rung 3), so this table holds only what is specific to the state.
// The state chip beside a rung's heading. Figure C4 prints the VALUE and the
// state together in one pill — "No limit · fallback", "No limit · set, not in
// effect" — because the two are one fact: what this layer says, and whether
// anyone is listening. Round 2 split them, leaving the value as body text and a
// pill reading only "set, not in effect", which says nothing about the layer.
// "● In effect" is rendered separately: every active rung carries it, locked or
// not, exactly as the figure draws rung 3 with both chips.
const LADDER_BADGE: Record<LadderRow['state'], { suffix: string | null; className: string }> = {
  'in-effect': { suffix: null, className: 'bg-iflow-100 text-iflow-700' },
  'locked-in-effect': { suffix: '🔒 Locked · in effect', className: 'bg-amber-100 text-amber-700' },
  'set-not-in-effect': { suffix: 'set, not in effect', className: 'bg-gray-100 text-gray-500' },
  blocked: { suffix: 'Blocked by the lock', className: 'bg-gray-100 text-gray-500' },
  'not-set': { suffix: null, className: 'bg-gray-100 text-gray-500' },
}

export default function WidestViewPolicy({
  value, locked, source, orgName, onChange, onLockChange,
  platformValue = 'no_limit', orgTypeValue = 'no_limit',
  policySet = false, policyUpdatedAt, policyUpdatedByName,
}: WidestViewPolicyProps) {
  const ladder = buildLadder(
    source, locked, orgName, platformValue, orgTypeValue, value, policySet,
    policyUpdatedAt, policyUpdatedByName,
  )
  // Every rung can be inactive: the resolver can answer a source this screen does
  // not know, and then no rung may claim to be deciding. The explanation still
  // has to appear — "we cannot tell you which layer won" is itself the answer —
  // so in that one case it falls back to a line under the ladder.
  const noRungActive = !ladder.some(
    (r) => r.state === 'in-effect' || r.state === 'locked-in-effect',
  )
  return (
    /* NO flex-1. This grew to fill the Focus card (round 4, to level the two
       columns), and growing is what pinned the card's divider and its
       "GET /preferences…" hint to the bottom edge: they are this element's
       SIBLINGS, so every pixel the ladder claimed pushed them further down.
       Round 8 fixed the symptom inside the list — the rungs stopped spreading —
       and the void simply moved, from between the rungs to between the last
       rung and the divider: 259px measured on the deployed page, where the
       figure leaves 14.5px.
       The card is still stretched to the right column's height, which is what
       the figure does. The difference is where the slack goes: BELOW the hint,
       as white under the last thing the card says, rather than as a hole
       punched between two things that belong together. */
    /* No border-b, no pb-5/mb-5 either. This element carried its own bottom
       rule AND 40px of padding+margin, and the Focus card's hint block below it
       carries a border-t and mt-4 of its own — so the card drew TWO faint rules
       17px apart where the figure draws one, and put 57px between the last rung
       and it where the figure leaves 14. The page owns that divider (it belongs
       to the hint, not to the ladder), so the duplicate goes and the spacing is
       set in one place. Used in exactly one screen, so nothing else re-lays. */
    <div data-part="WidestViewPolicy">
      <div className="flex items-baseline gap-2 mb-1">
        <h3 className="text-sm font-semibold text-gray-900">Widest view</h3>
        <span className="text-[11px] font-mono text-gray-400">widest_view</span>
      </div>

      {/* The figure prints no "<layer> in effect" line here. It said the same
          thing the ladder's own active rung says two inches below, and a reader
          who meets the claim twice has to work out whether they are the same
          claim. The ladder is the answer; this line was a second one. */}
      <div className="flex items-center gap-3 flex-wrap mt-2">
        <select
          aria-label="Widest view"
          value={value}
          onChange={(e) => onChange(e.target.value as 'entity' | 'portfolio' | 'no_limit')}
          className="light-input text-sm rounded-lg px-3 py-1.5"
        >
          {OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          type="button"
          role="switch"
          aria-checked={locked}
          onClick={() => onLockChange(!locked)}
          className={`relative w-10 h-6 rounded-full transition-colors ${locked ? 'bg-iflow-500' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-0.5 ${locked ? 'right-0.5' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow`} />
        </button>
        <span className="text-[12.5px] text-gray-700">
          {locked
            ? <><strong>Lock</strong>{' · on — ' + "members'" + ' personal choice is overridden'}</>
            : <><strong>Lock</strong>{' · off — a member may set their own value'}</>}
        </span>
      </div>

      {/* Figure C4's four-layer precedence ladder, drawn as the figure draws it:
          a numbered square badge and a mark per layer, the state as a chip, and
          the rung that is actually deciding ringed and tinted so it is found
          without reading. Most specific wins; a lock re-asserts the
          organisation layer. */}
      {/* A FIXED gap, not a distributed one. Round 5 gave this list `flex-1
          justify-between` so it would fill the Focus card and level the two
          columns — and inside a card 430px taller than the ladder, "fill" meant
          spreading the four rungs across it: the round-7 review measured ~130px
          between rung cards where the figure leaves ~45px, with the connector
          captions marooned in the middle of the empty space.
          
          Measured in Chrome against the built stylesheet: with no gap the cards
          sit 24.5px apart (the connector caption's own line box and padding),
          and each Tailwind step adds its own width — gap-4 gives 40.5px, gap-5
          gives 44.5px, gap-6 gives 48.5px.
          
          gap-1, not gap-5. Round 8 read the review's "~45px" as the target and
          took the nearest step; the FIGURE's own gaps, measured off it, are
          30.5 / 27.5 / 27.5 — mean 28.5 — and 28.5px is exactly what gap-1
          gives, because the caption's own line box and padding already supply
          24.5px of it. The figure's stylesheet says the same thing arithmetically:
          .keylayer{margin-bottom:8px} + .prec-cap{font-size:11.5px;margin:0 0 8px}.
          
          Whatever height the card has left over collects below the footer hint,
          where it is empty space rather than a hole punched through the middle
          of the argument. */}
      <ol data-testid="widest-view-precedence" className="mt-3 flex flex-col gap-1">
        {ladder.map((row, i) => {
          const active = row.state === 'in-effect' || row.state === 'locked-in-effect'
          const badge = LADDER_BADGE[row.state]
          return (
            <li key={row.n}>
              <div
                data-rung={row.n}
                data-state={row.state}
                className={`flex items-start gap-3 rounded-xl border px-3.5 py-2.5 ${
                  active
                    ? 'border-iflow-500 bg-gradient-to-br from-iflow-50 to-white ring-[3px] ring-iflow-500/15'
                    : 'border-gray-200 bg-white opacity-70'
                }`}
              >
                <span
                  aria-hidden
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[13px] font-extrabold ${
                    row.state === 'locked-in-effect' ? 'bg-amber-100 text-amber-700'
                      : active ? 'bg-iflow-500 text-white'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {row.n}
                </span>
                <div className="min-w-0 flex-1">
                  <h4 className="flex flex-wrap items-center gap-2 text-[13px] font-bold text-gray-900">
                    <span aria-hidden>{row.icon}</span>
                    {row.title}
                    {active && (
                      <span className="rounded-full bg-iflow-100 px-2 py-0.5 text-[10.5px] font-semibold text-iflow-700">
                        ● In effect
                      </span>
                    )}
                    <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${badge.className}`}>
                      {badge.suffix ? `${row.value} · ${badge.suffix}` : row.value}
                    </span>
                  </h4>
                  <p className="mt-1 text-[12px] leading-relaxed text-gray-500">{row.detail}</p>

                  {/* The explanation lives INSIDE the rung it explains. It used
                      to sit in a box under rung 4, which left the highlighted
                      card unable to say why it was highlighted — the one thing
                      the figure's active rung exists to do. */}
                  {active && (
                    <p className="mt-2 rounded-lg border border-iflow-100 bg-white/70 px-2.5 py-2 text-[11.5px] leading-relaxed text-iflow-800">
                      <strong>Why this is active:</strong> {whyThisIsActive(source, locked)}
                    </p>
                  )}
                </div>
              </div>
              {i < ladder.length - 1 && (
                <div className="py-1 text-center text-[11px] text-gray-400">
                  {LADDER_CONNECTOR[i]}
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {noRungActive && (
        <p className="mt-2 rounded-lg border border-iflow-100 bg-white/70 px-2.5 py-2 text-[11.5px] leading-relaxed text-iflow-800">
          <strong>Why this is active:</strong> {whyThisIsActive(source, locked)}
        </p>
      )}
    </div>
  )
}
